import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  QrCode2 as QrCodeIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import api from '../../utils/api';

interface TOTPSetupProps {
  onSetupComplete?: () => void;
  onError?: (error: string) => void;
}

function TOTPSetup({ onSetupComplete, onError }: TOTPSetupProps) {
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string>('');
  const [manualEntryKey, setManualEntryKey] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<'setup' | 'verify' | 'backup'>('setup');

  const handleStartSetup = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/totp/setup/begin');
      
      if (response.data.success) {
        setQrCode(response.data.qrCode);
        setManualEntryKey(response.data.manualEntryKey);
        setStep('verify');
      } else {
        onError?.('Failed to generate TOTP setup');
      }
    } catch (error: any) {
      console.error('Error starting TOTP setup:', error);
      const errorMessage = error.response?.data?.error || 'Failed to start TOTP setup';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndComplete = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      onError?.('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/totp/setup/finish', {
        token: verificationCode
      });

      if (response.data.success) {
        setBackupCodes(response.data.backupCodes);
        setStep('backup');
      } else {
        onError?.('Invalid verification code. Please try again.');
      }
    } catch (error: any) {
      console.error('Error verifying TOTP:', error);
      const errorMessage = error.response?.data?.error || 'Failed to verify code';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyManualKey = () => {
    navigator.clipboard.writeText(manualEntryKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadBackupCodes = () => {
    const text = backupCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'totp-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleComplete = () => {
    onSetupComplete?.();
  };

  const renderSetupStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
        Setup Authenticator App
      </Typography>
      
      <Alert 
        severity="info" 
        sx={{ 
          mb: 3,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          color: '#93c5fd'
        }}
      >
        <Typography variant="body2">
          You'll need an authenticator app like <strong>Microsoft Authenticator</strong>, 
          <strong> Google Authenticator</strong>, or <strong>Authy</strong> installed on your phone.
        </Typography>
      </Alert>

      <Button
        variant="contained"
        onClick={handleStartSetup}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : <QrCodeIcon />}
        fullWidth
        size="large"
        sx={{
          backgroundColor: '#4f46e5',
          '&:hover': { backgroundColor: '#4338ca' }
        }}
      >
        {loading ? 'Generating...' : 'Generate QR Code'}
      </Button>
    </Box>
  );

  const renderVerifyStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
        Scan QR Code
      </Typography>

      <Box sx={{ 
        textAlign: 'center', 
        mb: 3, 
        p: 2, 
        backgroundColor: 'white', 
        borderRadius: 2,
        display: 'inline-block',
        mx: 'auto',
        width: '100%'
      }}>
        {qrCode && (
          <img 
            src={qrCode} 
            alt="TOTP QR Code" 
            style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
          />
        )}
      </Box>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 2,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          color: '#93c5fd'
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Can't scan the QR code?</strong>
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Enter this key manually in your authenticator app:
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          p: 1,
          borderRadius: 1,
          fontFamily: 'monospace',
          wordBreak: 'break-all'
        }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {manualEntryKey}
          </Typography>
          <Button
            size="small"
            onClick={handleCopyManualKey}
            startIcon={copied ? <CheckIcon /> : <CopyIcon />}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </Box>
      </Alert>

      <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <Typography variant="body1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
        Enter the 6-digit code from your app:
      </Typography>

      <TextField
        fullWidth
        value={verificationCode}
        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        inputProps={{ 
          maxLength: 6,
          style: { 
            textAlign: 'center', 
            fontSize: '24px', 
            letterSpacing: '8px',
            fontFamily: 'monospace'
          }
        }}
        sx={{ 
          mb: 2,
          '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
          '& .MuiInputBase-input': { color: 'rgba(255, 255, 255, 0.95)' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
            '&.Mui-focused fieldset': { borderColor: '#6366f1' }
          }
        }}
      />

      <Button
        variant="contained"
        onClick={handleVerifyAndComplete}
        disabled={loading || verificationCode.length !== 6}
        fullWidth
        size="large"
        sx={{
          backgroundColor: '#4f46e5',
          '&:hover': { backgroundColor: '#4338ca' }
        }}
      >
        {loading ? <CircularProgress size={24} /> : 'Verify and Enable'}
      </Button>
    </Box>
  );

  const renderBackupStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
        Save Your Backup Codes
      </Typography>

      <Alert 
        severity="warning" 
        sx={{ 
          mb: 3,
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          color: '#fcd34d'
        }}
      >
        <Typography variant="body2">
          <strong>Important:</strong> Save these backup codes in a secure location. 
          Each code can only be used once if you lose access to your authenticator app.
        </Typography>
      </Alert>

      <Box sx={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.2)', 
        p: 2, 
        borderRadius: 1,
        mb: 2,
        maxHeight: '300px',
        overflowY: 'auto'
      }}>
        <List dense>
          {backupCodes.map((code, index) => (
            <ListItem key={index}>
              <ListItemText 
                primary={code}
                primaryTypographyProps={{
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.95)'
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleDownloadBackupCodes}
          startIcon={<DownloadIcon />}
          fullWidth
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.23)',
            color: 'rgba(255, 255, 255, 0.95)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Download
        </Button>
        <Button
          variant="outlined"
          onClick={handleCopyBackupCodes}
          startIcon={copied ? <CheckIcon /> : <CopyIcon />}
          fullWidth
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.23)',
            color: 'rgba(255, 255, 255, 0.95)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          {copied ? 'Copied!' : 'Copy All'}
        </Button>
      </Box>

      <Button
        variant="contained"
        onClick={handleComplete}
        fullWidth
        size="large"
        sx={{
          backgroundColor: '#10b981',
          '&:hover': { backgroundColor: '#059669' }
        }}
      >
        I've Saved My Backup Codes
      </Button>
    </Box>
  );

  return (
    <Box>
      {step === 'setup' && renderSetupStep()}
      {step === 'verify' && renderVerifyStep()}
      {step === 'backup' && renderBackupStep()}
    </Box>
  );
}

export default TOTPSetup;
